/* Approval flow is quite complex and can be confusing. There are two main flows:
1. Bulk Approval Flow
2. Single Approval Flow

Both flows have to account for product sku and parent sku mapping.

Generally single approval flow always has a dialog to indicate action meaning a dialog always shows even if its a simple approve.
If there is a mapping already exisitng, etc...

Bulk approval tries to skip certain things to speed up the process and require less user interaction.
For example:
- If a mapping already exists, it will just approve the product.
- If there is no dupe products and no mapping, it will just approve the product.

If there exists a mapping we generally just take it and go.
*/

import React, {useEffect, useState} from "react";
import {approveProduct as utilApproveProduct, getProduct as utilGetProduct, getAttributes as utilGetAttributes, getAttributeSets as utilGetAttributeSets, utilAddInventory, utilCheckInventory, utilGetPartnerSettings, utilSearchRetailerCatalog, isNullOrEmpty} from "../../../../utils/utils";
import { Button, Dialog, Grid, Typography } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import ContentLoader from "../../../../components/content_loader";
import {ProductColumn} from "../../containers/catalog/components/catalog_columns";
import Auth from "../../../../auth";
import EnhancedTextField from "../../../../components/EnhancedTextField";
import { pageContentGap } from "../../../../theme";

interface PartnerSettings {
    caseSensitive: boolean;
    autoMatch: boolean;
}

interface InventoryMapping {
    MerchantSKU?: string;
    merchantSku?: string;
    supplierSku?: string;
    supplierId?: number;
}

interface Product {
    sku: string;
    parentSku?: string;
    supplierId: number;
    supplierName: string;
    approvalStatus: string;
    image?: string;
    name?: string;
}

interface RetailerProduct {
    Id?: string;
    Attributes?: {
        sku?: string;
        image?: string;
        name?: string;
        parent_sku?: string;
    };
    sku?: string;
    parentSku?: string;
}

interface ApprovalResult {
    success: boolean;
    data: string | null;
    error: string | null;
}

interface ErrorState {
    open: boolean;
    errorHeader: string;
    errorMessage: string;
}

interface ProductApproveDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onApprove: (success: boolean) => void;
    setUpdating: (updating: boolean) => void;
    setError: (error: ErrorState) => void;
    approveAndList?: boolean;
    product: Product | null;
    isRetailer?: boolean;
    currentCatalog?: any;
    forceUniqueRetailerSku?: boolean;
}

export const compareSkus = (sku1: string | null | undefined, sku2: string | null | undefined, caseSensitive: boolean): boolean => {
    if (!sku1 || !sku2) return sku1 === sku2;
    return caseSensitive ? sku1 === sku2 : sku1.toUpperCase() === sku2.toUpperCase();
}

type RetailerRulesLoad =
    | {status: "skipped"}
    | {status: "error"}
    | {status: "ok", rules: {attributes: any[], attributeSetsBySupplier: Record<string, any[]>}};

export const loadRetailerRules = async (product: Product | null): Promise<RetailerRulesLoad> => {
    if (product === null) return {status: "skipped"};
    const supplierId = product.supplierId;
    try {
        const [attributes, attributeSets] = await Promise.all([
            utilGetAttributes(supplierId, true),
            utilGetAttributeSets(true, supplierId, null, true)
        ]);
        // These resolve to an error object rather than throwing. An error status must not yield
        // rules, so the caller leaves retailerRules null and approval stays refused.
        if (!Array.isArray(attributes) || !Array.isArray(attributeSets)) return {status: "error"};
        return {status: "ok", rules: {attributes, attributeSetsBySupplier: {[String(supplierId)]: attributeSets}}};
    } catch (error) {
        return {status: "error"};
    }
};

const updateInventory = async (product: Product, changedSku: string | undefined, inventory: InventoryMapping | null, partnerSettings: PartnerSettings = { caseSensitive: false, autoMatch: false }): Promise<ApprovalResult> => {
    let usedSku = changedSku === "" ? product.sku : changedSku;
    let originalSku: string | undefined = product.sku;

    // If case sensitivity is off, uppercase the SKUs
    if (!partnerSettings.caseSensitive) {
        usedSku = usedSku ? usedSku.toUpperCase() : usedSku;
        originalSku = originalSku ? originalSku.toUpperCase() : originalSku;
    }

    const runStatus: any = await utilAddInventory(product.supplierId, usedSku, originalSku, inventory);
    if(runStatus.errorMessage) {
        return {
            success: false,
            data: null,
            error: runStatus.errorMessage
        };
    }
    else {
        return {
            success: true,
            data: null,
            error: null
        }
    }
}

const updateParentSkuInventory = async (product: Product, changedParentSku: string | undefined, partnerSettings: PartnerSettings = { caseSensitive: false, autoMatch: false }): Promise<ApprovalResult> => {
    let usedParentSku = changedParentSku === "" ? product.parentSku : changedParentSku;
    let originalParentSku: string | undefined = product.parentSku;

    // If case sensitivity is off, uppercase the SKUs
    if (!partnerSettings.caseSensitive) {
        usedParentSku = usedParentSku ? usedParentSku.toUpperCase() : usedParentSku;
        originalParentSku = originalParentSku ? originalParentSku.toUpperCase() : originalParentSku;
    }

    const runStatus: any = await utilAddInventory(product.supplierId, usedParentSku, originalParentSku);
    if(runStatus.errorMessage) {
        return {
            success: false,
            data: null,
            error: runStatus.errorMessage
        };
    }
    else {
        return {
            success: true,
            data: null,
            error: null
        }
    }
}

const approveProduct = async (status: string, changedSku: string | undefined, product: Product, duplicateProduct: RetailerProduct | null, inventory: InventoryMapping | null, parentSku: string | undefined, retailerRules: {attributes: any[], attributeSetsBySupplier: Record<string, any[]>}, partnerSettings: PartnerSettings = { caseSensitive: false, autoMatch: false }): Promise<ApprovalResult> => {
    if (product.approvalStatus === "Approved") return {
        success: false,
        data: null,
        error: "Product is already approved!"
    };

    // If case sensitivity is off, uppercase the SKUs
    const processedSku = partnerSettings.caseSensitive ? product.sku : product.sku.toUpperCase();
    const processedParentSku = partnerSettings.caseSensitive ? parentSku : (parentSku || '').toUpperCase();

    if (duplicateProduct) {
        if (inventory) {
            let usedSku = !isNullOrEmpty(inventory.merchantSku) ? inventory.merchantSku : duplicateProduct.Id;
            usedSku = partnerSettings.caseSensitive ? usedSku : (usedSku || '').toUpperCase();
            return await utilApproveProduct(processedSku, product.supplierId, product.supplierName, status, true, usedSku, processedParentSku, retailerRules);
        } else {
            let usedSku: string | undefined = changedSku === "" ? undefined : changedSku;
            if (usedSku) {
                usedSku = partnerSettings.caseSensitive ? usedSku : usedSku.toUpperCase();
            }
            return await utilApproveProduct(processedSku, product.supplierId, product.supplierName, status, true, usedSku, processedParentSku, retailerRules);
        }
    } else {
        if (inventory) {
            let merchantSku: string | undefined = inventory.merchantSku;
            // Treat empty string as no merchantSku
            if (isNullOrEmpty(merchantSku)) {
                merchantSku = undefined;
            } else {
                merchantSku = partnerSettings.caseSensitive ? merchantSku : merchantSku!.toUpperCase();
            }
            return await utilApproveProduct(processedSku, product.supplierId, product.supplierName, status, true, merchantSku, processedParentSku, retailerRules);
        } else {
            return await utilApproveProduct(processedSku, product.supplierId, product.supplierName, status, true, undefined, processedParentSku, retailerRules);
        }
    }

}

const approvalCheck = async (product: Product, productInventory: InventoryMapping | null, retailerProduct: RetailerProduct | null): Promise<{ partnerSettings: PartnerSettings }> => {
    const partnerSettings: PartnerSettings = await utilGetPartnerSettings(product.supplierId) as PartnerSettings;
    return {partnerSettings};
}

export const headlessApprovalFlow = async (
    product: Product,
    retailerProduct: RetailerProduct | null,
    productInventory: InventoryMapping | null,
    retailerProductParentSkuMappings: any,
    parentSkuMapping: InventoryMapping | null,
    retailerProductMappedSku: RetailerProduct | null,
    andList: boolean,
    retailerRules: {attributes: any[], attributeSetsBySupplier: Record<string, any[]>},
    forceUniqueRetailerSku: boolean = false
): Promise<ApprovalResult> => {
    const {partnerSettings} = await approvalCheck(product, productInventory, retailerProduct);
    const syncStatus = andList ? "Listing Pending" : "Not Listed";
    const updatedRetailerProduct = retailerProductMappedSku ? retailerProductMappedSku : retailerProduct;

    // Parent SKU Approval Flow
    // Rule 1: Use mapping if available, Rule 2: Use existing SKU, Rule 3: Apply pipe if override on
    let changedParentSku: string | undefined = product.parentSku;
    if (parentSkuMapping) {
        // Rule 1: Use existing mapping
        changedParentSku = parentSkuMapping.merchantSku;
    } else if (forceUniqueRetailerSku && product.parentSku) {
        // Rule 3: No mapping + override on → apply pipe
        // Parent SKUs are shared by siblings, so no duplicate check needed (unlike product SKUs which must be unique)
        changedParentSku = product.parentSku + "|" + product.supplierId;
        await updateParentSkuInventory(product, changedParentSku, partnerSettings);
    } else if (retailerProductParentSkuMappings) {
        return {
            success: true,
            data: "manual_review",
            error: null
        }
    } else if (!partnerSettings.autoMatch) {
        await updateParentSkuInventory(product, product.parentSku, partnerSettings);
        changedParentSku = product.parentSku;
    }

    let inventoryRunStatus: ApprovalResult | undefined;
    let approvedProductRunStatus: ApprovalResult | undefined;

    // SKU Approval Flow
    // Rule 1: Use mapping if available, Rule 2: Use existing SKU, Rule 3: Apply pipe if override on
    if (forceUniqueRetailerSku && (!productInventory || !productInventory.merchantSku)) {
        // No mapping + override on → apply pipe
        const pipedSku = product.sku + "|" + product.supplierId;

        // Rule 4: Check if piped SKU already exists in retailer catalog
        const pipedSkuCheck = await utilSearchRetailerCatalog(`$filter=(sku eq '${pipedSku}')`);
        if (pipedSkuCheck && pipedSkuCheck.data && pipedSkuCheck.data.length > 0) {
            return { success: true, data: "manual_review", error: null };
        }

        // Create inventory mapping
        inventoryRunStatus = await updateInventory(product, pipedSku, productInventory, partnerSettings);

        // Approve with piped SKU directly (bypass approveProduct to ensure piped SKU is used)
        if (product.approvalStatus === "Approved") {
            return { success: false, data: null, error: "Product is already approved!" };
        }
        const processedSku = partnerSettings.caseSensitive ? product.sku : product.sku.toUpperCase();
        const processedPipedSku = partnerSettings.caseSensitive ? pipedSku : pipedSku.toUpperCase();
        const processedParentSku = partnerSettings.caseSensitive ? changedParentSku : (changedParentSku || '').toUpperCase();
        approvedProductRunStatus = await utilApproveProduct(processedSku, product.supplierId, product.supplierName, syncStatus, true, processedPipedSku, processedParentSku, retailerRules);
    } else if (updatedRetailerProduct) {
        if (!productInventory) {
            if (partnerSettings.autoMatch) {
                // (Add to retailer catalog with SKU) (No inventory changes)
                approvedProductRunStatus = await approveProduct(syncStatus, undefined, product, updatedRetailerProduct, productInventory, changedParentSku, retailerRules, partnerSettings);
            } else {
                // Ask for option
                //add new merchant sku (Add to retailer catalog with new SKU) (Update inventory new SKU)
                //overwrite existing product (Overwrite retailer product with SKU) (Update inventory SKU)
                return {
                    success: true,
                    data: "manual_review",
                    error: null
                }
            }
        } else {
            // Retailer product exsits with the inventory merchantSku OR no merchant SKU
            if (!productInventory.merchantSku && !partnerSettings.autoMatch) {
                // Ask to overwrite
                //yes (Overwrite retailer product with SKU) (No inventory changes)
                //no (Do nothing)
                return {
                    success: true,
                    data: "manual_review",
                    error: null
                }
            } else {
                // (Add to retailer catalog with SKU) (No inventory changes unless auto match is disabled)
                if (!productInventory.merchantSku && !partnerSettings.autoMatch) {
                    inventoryRunStatus = await updateInventory(product, product.sku, productInventory, partnerSettings);
                }

                // Use the merchant SKU if it exists and is not empty, otherwise use undefined
                const merchantSkuToUse = !isNullOrEmpty(productInventory.merchantSku) ? productInventory.merchantSku : undefined;
                approvedProductRunStatus = await approveProduct(syncStatus, merchantSkuToUse, product, updatedRetailerProduct, productInventory, changedParentSku, retailerRules, partnerSettings);
            }
        }
    } else {
        if (productInventory) {
            let changedSku: string | undefined = ""

            if (!productInventory.merchantSku && !partnerSettings.autoMatch) {
                // (Add to catalog) (Update inventory merchant SKU)
                inventoryRunStatus = await updateInventory(product, product.sku, productInventory, partnerSettings);
                changedSku = undefined
            } else {
                // (Add to retailer catalog with merchant SKU) (No inventory changes)
                changedSku = productInventory.merchantSku;
            }

            approvedProductRunStatus = await approveProduct(syncStatus, changedSku, product, updatedRetailerProduct, productInventory, changedParentSku, retailerRules, partnerSettings);
        } else {
            // (Add to retailer catalog) (No inventory changes)
            if (partnerSettings.autoMatch) {
                approvedProductRunStatus = await approveProduct(syncStatus, undefined, product, updatedRetailerProduct, productInventory, changedParentSku, retailerRules, partnerSettings);
            } else {
                inventoryRunStatus = await updateInventory(product, product.sku, productInventory, partnerSettings);
                approvedProductRunStatus = await approveProduct(syncStatus, undefined, product, updatedRetailerProduct, productInventory, changedParentSku, retailerRules, partnerSettings);
            }
        }
    }

    let inventoryErrorMessage = inventoryRunStatus ? inventoryRunStatus.error ?? "" : "";
    let approvedProductErrorMessage = approvedProductRunStatus ? approvedProductRunStatus.error ?? "" : "";
    let errorMessage =
        inventoryErrorMessage
        + ((inventoryErrorMessage && approvedProductErrorMessage) && " | ") +
        approvedProductErrorMessage;

    let inventorySuccess = inventoryRunStatus ? inventoryRunStatus.success : true;
    let approvedProductSuccess = approvedProductRunStatus ? approvedProductRunStatus.success : true;
    let success = inventorySuccess && approvedProductSuccess;

    return {
        success: success,
        data: null,
        error: errorMessage
    }
}

function ProductApproveDialog(props: ProductApproveDialogProps) {
    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");

    const theme = useTheme();

    const {open, setOpen, onApprove} = props;
    const {setUpdating, setError} = props;
    const {approveAndList} = props; // default option to approve and list

    const [loading, setLoading] = React.useState<boolean>(false);
    const [duplicateProduct, setDuplicateProduct] = React.useState<RetailerProduct | null>(null);
    const [inventory, setInventory] = React.useState<InventoryMapping | null>(null);
    const [parentSkuInventory, setParentSkuInventory] = React.useState<InventoryMapping | null>(null);
    const [existingParentSkuProducts, setExistingParentSkuProducts] = React.useState<Product[] | null>(null);
    const [contextMessage, setContextMessage] = useState<React.ReactNode>("");
    const [partnerSettings, setPartnerSettings] = useState<PartnerSettings>({caseSensitive: false, autoMatch: false});
    const [retailerRules, setRetailerRules] = useState<{attributes: any[], attributeSetsBySupplier: Record<string, any[]>} | null>(null);

    const [changedSku, setChangedSku] = useState<string>("");
    const [changedParentSku, setChangedParentSku] = useState<string>("");
    const [changeSkuDialog, setChangeSkuDialog] = useState<boolean>(false);
    const [isChangingParentSku, setIsChangingParentSku] = useState<boolean>(false);
    const savedSkuRef = React.useRef<string>("");
    const savedParentSkuRef = React.useRef<string>("");

    const updateInventory = async (updateSkuInventory: boolean, updateParentSkuInventory: boolean) => {
        if (partnerSettings.autoMatch && !props.forceUniqueRetailerSku) return;

        // Update inventory for sku
        if (updateSkuInventory) {
            let usedSku = changedSku === "" ? props.product!.sku : changedSku;
            let originalSku = props.product!.sku;

            // If case sensitivity is off, uppercase the SKUs
            if (!partnerSettings.caseSensitive) {
                usedSku = usedSku ? usedSku.toUpperCase() : usedSku;
                originalSku = originalSku ? originalSku.toUpperCase() : originalSku;
            }

            const response: any = await utilAddInventory(props.product!.supplierId, usedSku, originalSku, inventory);
            if (response.errorHeader) {
                setError(response);
                return;
            }
        }

        // Update inventory for parent sku
        if(updateParentSkuInventory) {
            if (!parentSkuInventory && props.product!.parentSku) {
                let usedParentSku = changedParentSku === "" ? (props.product!.parentSku ?? "") : changedParentSku;
                let originalParentSku = props.product!.parentSku;

                // If case sensitivity is off, uppercase the SKUs
                if (!partnerSettings.caseSensitive) {
                    usedParentSku = usedParentSku ? usedParentSku.toUpperCase() : usedParentSku;
                    originalParentSku = originalParentSku ? originalParentSku.toUpperCase() : originalParentSku;
                }

                const response: any = await utilAddInventory(props.product!.supplierId, usedParentSku, originalParentSku);
                if (response.errorHeader) {
                    setError(response);
                    return;
                }
            }
        }
    }
    const approveProduct = async (status: string) => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }

        if (props.product!.approvalStatus === "Approved") {
            return;
        }

        if (retailerRules === null) {
            setError({
                open: true,
                errorHeader: "Validation Rules Unavailable",
                errorMessage: "The catalog validation rules could not be loaded, so this product cannot be approved yet. Please try again."
            });
            return;
        }

        setOpen(false);
        setUpdating(true);

        let runStatus: any;
        let usedParentSku = changedParentSku === "" ? (parentSkuInventory?.MerchantSKU ?? props.product!.parentSku) : changedParentSku;
        let usedSku = changedSku === "" ? (inventory?.MerchantSKU ?? props.product!.sku) : changedSku;

        // If case sensitivity is off, uppercase the SKUs
        const processedProductSku = partnerSettings.caseSensitive ? props.product!.sku : props.product!.sku.toUpperCase();
        if (!partnerSettings.caseSensitive) {
            usedParentSku = usedParentSku ? usedParentSku.toUpperCase() : usedParentSku;
            usedSku = usedSku ? usedSku.toUpperCase() : usedSku;
        }

        if (duplicateProduct || props.forceUniqueRetailerSku) {
            // When forceUniqueRetailerSku is on, always use changedSku (the piped SKU)
            // When duplicateProduct exists, use the user-selected/changed SKU
            runStatus = await utilApproveProduct(processedProductSku, props.product!.supplierId, props.product!.supplierName, status, true, usedSku, usedParentSku, retailerRules);
        } else {
            if (inventory) {
                const merchantSku = partnerSettings.caseSensitive ? inventory.MerchantSKU : (inventory.MerchantSKU ? inventory.MerchantSKU.toUpperCase() : undefined);
                runStatus = await utilApproveProduct(processedProductSku, props.product!.supplierId, props.product!.supplierName, status, true, merchantSku, usedParentSku, retailerRules)
            } else {
                runStatus = await utilApproveProduct(processedProductSku, props.product!.supplierId, props.product!.supplierName, status, true, undefined, usedParentSku, retailerRules);
            }
        }

        if (!runStatus.success) setError({
            open: true,
            errorHeader: "Error Approving Product!",
            errorMessage: runStatus.error
        });
        await new Promise(r => setTimeout(r, 750));
        onApprove(runStatus.success);
        setUpdating(false);
    }
    const updateInventoryAndApprove = async (status: string, updateSkuInventory: boolean, updateParentSkuInventory: boolean) => {
        if (retailerRules === null) {
            setError({
                open: true,
                errorHeader: "Validation Rules Unavailable",
                errorMessage: "The catalog validation rules could not be loaded, so this product cannot be approved yet. Please try again."
            });
            return;
        }
        setOpen(false);
        setUpdating(true);
        await updateInventory(updateSkuInventory, updateParentSkuInventory);
        await approveProduct(status);
        setUpdating(false);
    }

    useEffect(() => {
        const getRetailerRules = async () => {
            const result = await loadRetailerRules(props.product);
            if (result.status === "error") {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: "An error occurred while trying to load the catalog validation rules."
                });
                return;
            }
            if (result.status === "ok") setRetailerRules(result.rules);
        }

        const getPartnerSettings = async () => {
            try {
                const partnerSettings = await utilGetPartnerSettings(props.product!.supplierId) as PartnerSettings;
                setPartnerSettings(partnerSettings);
                return partnerSettings;
            } catch (error) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: "An error occurred while trying to get partner settings."
                });
            }
        }
        const approvalChecks = async (partnerSettings: PartnerSettings) => {
            let retailerProduct: RetailerProduct | null = null;
            let inventory: InventoryMapping | null = null;

            try {
                // Check for retailer product
                let foundProduct = await utilGetProduct(props.product!.sku, undefined, false, props.isRetailer);
                if (foundProduct.errorStatus === 404) retailerProduct = null;
                else if (foundProduct.errorMessage) throw new Error(foundProduct.errorMessage);
                else retailerProduct = foundProduct;

                // Check for inventory
                const foundInventory = await utilCheckInventory(props.product!.supplierId, props.product!.sku);
                if (foundInventory.errorStatus === 404) inventory = null;
                else if (foundInventory.errorMessage) throw new Error(foundInventory.errorMessage);
                else inventory = foundInventory;

                // If there is a merchant SKU mapping different from the product SKU, check if that product exists in retailer catalog
                let retailerProductUsingMerchantSku: RetailerProduct | null = null;
                let hasDifferingMerchantSku = inventory && (!compareSkus(inventory.MerchantSKU, props.product!.sku, partnerSettings.caseSensitive) && !isNullOrEmpty(inventory.MerchantSKU));
                if (inventory && hasDifferingMerchantSku) {
                    const response = await utilGetProduct(inventory.MerchantSKU, undefined, false, props.isRetailer);
                    if (response.errorStatus === 404) { /* Do nothing */ }
                    else if (response.errorMessage) throw new Error(response.errorMessage);
                    else {
                        retailerProductUsingMerchantSku = response;
                    }
                }

                //  Parent SKU checks
                const foundParentSkuInventory = await utilCheckInventory(props.product!.supplierId, props.product!.parentSku);
                if (!foundParentSkuInventory.errorStatus) {
                    setParentSkuInventory(foundParentSkuInventory);
                } else {
                    const foundParentSkuProducts = await utilSearchRetailerCatalog(`$filter=(parentSku eq '${props.product!.parentSku}')`)
                    if (foundParentSkuProducts.data.length > 0) setExistingParentSkuProducts(foundParentSkuProducts.data);
                }

                setInventory(inventory);

                // If forceUniqueRetailerSku is enabled, handle piped SKU logic BEFORE normal conflict checks
                if (props.forceUniqueRetailerSku) {
                    const pipedSku = props.product!.sku + "|" + props.product!.supplierId;

                    // For product SKU: use mapped merchantSku if available, otherwise pipe
                    if (!inventory || isNullOrEmpty(inventory.MerchantSKU)) {
                        // No mapping exists — auto-apply piped SKU
                        setChangedSku(pipedSku);

                        // Only show overwrite dialog if the piped SKU itself already exists in the retailer catalog
                        let pipedSkuExists = false;
                        try {
                            const pipedSkuResults = await utilSearchRetailerCatalog(`$filter=(sku eq '${pipedSku}')`);
                            if (pipedSkuResults && pipedSkuResults.data && pipedSkuResults.data.length > 0) {
                                pipedSkuExists = true;
                                const item = pipedSkuResults.data[0];
                                setDuplicateProduct({
                                    ...item,
                                    Attributes: {
                                        sku: item.sku,
                                        image: item.image,
                                        name: item.name,
                                        parent_sku: item.parentSku,
                                    }
                                });
                                setContextMessage(
                                    <>
                                        The piped SKU <b>{pipedSku}</b> already exists in the retailer's catalog.
                                        Would you like to overwrite the existing product?
                                    </>
                                );
                            }
                        } catch {
                            pipedSkuExists = false;
                        }

                        if (!pipedSkuExists) {
                            setDuplicateProduct(null);
                        }
                    } else {
                        // Mapped merchantSku exists — use it, clear conflict
                        setDuplicateProduct(null);
                    }

                    // For parent SKU: use mapped parentSku if available, otherwise pipe
                    if (foundParentSkuInventory && foundParentSkuInventory.MerchantSKU) {
                        // Mapping exists — use it, no conflict
                        setExistingParentSkuProducts(null);
                    } else if (props.product!.parentSku) {
                        // Parent SKUs are shared by siblings, so no duplicate check needed
                        // Just apply the piped parent SKU
                        const pipedParentSku = props.product!.parentSku + "|" + props.product!.supplierId;
                        setChangedParentSku(pipedParentSku);
                        setExistingParentSkuProducts(null);
                    } else {
                        setExistingParentSkuProducts(null);
                    }
                } else {
                    // Normal flow (no forceUniqueRetailerSku)
                    if(hasDifferingMerchantSku && retailerProductUsingMerchantSku) {
                        setDuplicateProduct(retailerProductUsingMerchantSku);
                    } else {
                        setDuplicateProduct(retailerProduct);
                    }

                    // Check how to handle approval
                    if ((hasDifferingMerchantSku && retailerProductUsingMerchantSku) || (retailerProduct && !hasDifferingMerchantSku)) {
                        setContextMessage(
                            <>
                                {inventory && ((!compareSkus(inventory.MerchantSKU, props.product!.sku, partnerSettings.caseSensitive)) && !isNullOrEmpty(inventory.MerchantSKU)) ? (
                                    <>
                                        The SKU you're trying to approve is already linked to a retailer SKU that exists in the retailer's catalog.
                                        Would you like to overwrite the existing product or enter a different SKU?
                                        <br />
                                        Supplier SKU <b>{props.product!.sku}</b> is mapped to Retailer SKU <b>{inventory.MerchantSKU}</b>.

                                        {foundParentSkuInventory && foundParentSkuInventory.MerchantSKU ? (
                                            <>
                                                <br />
                                                Supplier Parent SKU <b>{props.product!.parentSku}</b> is mapped to Retailer Parent SKU <b>{foundParentSkuInventory.MerchantSKU}</b>.
                                            </>
                                        ) : <></>}
                                    </>
                                ) : (
                                    <>
                                        This SKU is already in the retailer's catalog.

                                        <ul>
                                            <li>Select "Overwrite" to replace the existing variant with the new one.</li>
                                            <li>Select "Change SKU" to assign a new retailer SKU to the new variant and
                                                create a separate item in the catalog.</li>
                                        </ul>
                                    </>
                                )}
                            </>
                        )
                    }
                }

            } catch (error) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: "An error occurred while trying to approve the product."
                });
            }
        }
        const init = async () => {
            setLoading(true);
            const partnerSettings = await getPartnerSettings();
            await approvalChecks(partnerSettings);
            setLoading(false);
        }

        setDuplicateProduct(null);
        setInventory(null);
        setParentSkuInventory(null);
        setExistingParentSkuProducts(null);
        setContextMessage("");
        setPartnerSettings({caseSensitive: false, autoMatch: false});
        // Cleared so a refetch that fails on reopen cannot approve against the previous rule set.
        setRetailerRules(null);

        setChangedSku("");
        setChangedParentSku("");
        setChangeSkuDialog(false);

        if (props.product === null) return;
        getRetailerRules();
        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    const headerRender = () => {
        if (duplicateProduct !== null && !parentSkuInventory && existingParentSkuProducts) {
            return (
                <>
                    <Typography variant={"h5"}>Existing SKU and Parent SKU Found!</Typography>
                    <Typography variant={"body1"}>Both the SKU and parent SKU already exist in the retailer's catalog. Please resolve both conflicts.</Typography>
                </>
            )
        } else if (duplicateProduct !== null) {
            return (
                <>
                    <Typography variant={"h5"}>Existing SKU Found!</Typography>
                    <Typography variant={"body1"}>Would you like to overwrite existing variant or change SKU?</Typography>
                </>
            )
        } else if (!parentSkuInventory && existingParentSkuProducts) {
            return (
                <>
                    <Typography variant={"h5"}>Existing Parent Product found!</Typography>
                    <Typography variant={"body1"}>
                        Your variant's product data includes a parent SKU that matches an existing parent product in the
                        retailer's catalog.
                        <ul style={{margin: 0}}>
                            <li>Select "Add to Existing" to link the new variant to the existing parent product.</li>
                            <li>Select "Create New" to change the variant's parent SKU and create a separate product.</li>
                        </ul>
                    </Typography>
                </>
            )
        } else {
            return (
                <>
                    <Typography variant={"h5"}>Approve Product</Typography>
                    <Typography variant={"body1"} style={{display: "block"}}>
                        Approving SKU(s) will copy their details to the retailer's catalog.
                        <ul>
                            <li>Click "Approve" to add the SKU(s) to the catalog while keeping them in a "Not Listed"
                            status for future listing.</li>
                            <li>Click "Approve & List" to approve the SKU(s) and immediately list them to your eCommerce system.</li>
                        </ul>
                        {contextMessage && <><br/> <br/></>}
                        {contextMessage}
                    </Typography>
                </>
            )
        }
    }

    const bodyRender = () => {
        if (duplicateProduct !== null && !parentSkuInventory && existingParentSkuProducts) {
            return (
                <>
                    <Grid container>
                        <Grid container item size={12} style={{gap: "5px"}}>
                            <Grid item style={{flex: 1}}>
                                <Typography style={{fontSize: "14px"}}>Existing SKU: {duplicateProduct.Attributes.sku}</Typography>
                                <ProductColumn style={{paddingBottom: "10px"}}
                                               row={{
                                                   sku: duplicateProduct.Attributes.sku,
                                                   image: duplicateProduct.Attributes.image,
                                                   name: duplicateProduct.Attributes.name,
                                                   parentSku: duplicateProduct.Attributes.parent_sku,
                                               }}
                                               column={{id: 0}}
                                               noFilter={true}
                                />
                            </Grid>
                        </Grid>

                        <Grid container item size={12} style={{marginTop: "0.5em", gap: "5px", flexDirection: "column"}}>
                            <Typography style={{fontSize: "14px"}}>Found products with same Parent SKU:</Typography>
                            <Grid container>
                                {existingParentSkuProducts.map((product: Product, index: number) => (
                                    <ProductColumn key={index} style={{paddingBottom: "10px"}}
                                                   row={{
                                                       sku: product.sku,
                                                       image: product.image,
                                                       name: product.name,
                                                       parentSku: product.parentSku,
                                                   }}
                                                   column={{id: 0}}
                                                   noFilter={true}
                                    />
                                ))}
                            </Grid>
                        </Grid>

                        <Typography variant={"body1"} style={{display: "block", marginTop: "0.5em"}}>
                            {contextMessage}
                        </Typography>
                    </Grid>
                    <Grid item container justifyContent={"flex-end"}
                          style={{borderTop: "1px solid #DCDCDC", paddingTop: "12px", gap: pageContentGap, width: "100%"}}>
                        <Button variant="text"
                                onClick={() => setOpen(false)}>
                            Cancel
                        </Button>

                        <Button variant="text" color="primary"
                                onClick={() => {
                                    const shouldUpdateInventory = props.forceUniqueRetailerSku
                                        ? changedSku !== ""
                                        : !inventory || (inventory && !inventory.MerchantSKU);
                                    const shouldUpdateParentSkuInventory = props.forceUniqueRetailerSku
                                        ? changedParentSku !== ""
                                        : !parentSkuInventory || (parentSkuInventory && !parentSkuInventory.MerchantSKU);
                                    const status = approveAndList ? "Listing Pending" : "Not Listed";
                                    updateInventoryAndApprove(status, shouldUpdateInventory, shouldUpdateParentSkuInventory);
                                }}>
                            Overwrite
                        </Button>

                        <Button variant="text" color="primary"
                            disabled={partnerSettings.autoMatch}
                            onClick={() => {
                                savedSkuRef.current = changedSku;
                                savedParentSkuRef.current = changedParentSku;
                                setChangeSkuDialog(true);
                                setChangedSku(props.product!.sku + "|" + props.product!.supplierId);
                                setChangedParentSku(props.product!.parentSku + "|" + props.product!.supplierId);
                                setIsChangingParentSku(false);
                            }}>
                            Change SKUs
                        </Button>
                    </Grid>
                </>
            );
        } else if (duplicateProduct !== null) {
            return (
                <>
                    <Grid container>
                        <Grid container item size={12} style={{gap: "5px"}}>
                            <Grid item style={{flex: 1}}>
                                <Typography style={{fontSize: "14px"}}>Existing SKU: {duplicateProduct.Attributes.sku}</Typography>
                                <ProductColumn style={{paddingBottom: "10px"}}
                                               row={{
                                                   sku: duplicateProduct.Attributes.sku,
                                                   image: duplicateProduct.Attributes.image,
                                                   name: duplicateProduct.Attributes.name,
                                                   parentSku: duplicateProduct.Attributes.parent_sku,
                                               }}
                                               column={{id: 0}}
                                               noFilter={true}
                                />
                            </Grid>
                        </Grid>

                        <Grid container item size={12} style={{marginTop: "0.5em", gap: "5px"}}>
                            <Grid item style={{flex: 1}}>
                                <Typography style={{fontSize: "14px"}}>Newly Introduced SKU: {props.product!.sku}</Typography>
                                <ProductColumn style={{paddingBottom: "10px"}}
                                               row={{
                                                   sku: props.product!.sku,
                                                   image: props.product!.image,
                                                   name: props.product!.name,
                                                   parentSku: props.product!.parentSku,
                                               }}
                                               column={{id: 0}}
                                               noFilter={true}
                                />
                            </Grid>
                        </Grid>

                        <Typography variant={"body1"} style={{display: "block", marginTop: "0.5em"}}>
                            {contextMessage}
                        </Typography>
                    </Grid>
                    <Grid item container justifyContent={"flex-end"}
                          style={{borderTop: "1px solid #DCDCDC", paddingTop: "12px", gap: pageContentGap, width: "100%"}}>
                        <Button variant="text"
                                onClick={() => setOpen(false)}>
                            Cancel
                        </Button>

                        <Button variant="text" color="primary"
                                onClick={() => {
                                    const shouldUpdateInventory = props.forceUniqueRetailerSku
                                        ? changedSku !== ""
                                        : !inventory || (inventory && !inventory.MerchantSKU);
                                    const shouldUpdateParentSkuInventory = props.forceUniqueRetailerSku
                                        ? changedParentSku !== ""
                                        : !parentSkuInventory || (parentSkuInventory && !parentSkuInventory.MerchantSKU);
                                    const status = approveAndList ? "Listing Pending" : "Not Listed";
                                    updateInventoryAndApprove(status, shouldUpdateInventory, shouldUpdateParentSkuInventory);
                                }}>
                            Overwrite
                        </Button>

                        <Button variant="text" color="primary"
                            disabled={partnerSettings.autoMatch}
                            onClick={() => {
                                savedSkuRef.current = changedSku;
                                savedParentSkuRef.current = changedParentSku;
                                setChangeSkuDialog(true);
                                if (!props.forceUniqueRetailerSku) {
                                    setChangedParentSku(props.product!.parentSku);
                                    setChangedSku(props.product!.sku + "|" + props.product!.supplierId);
                                }
                                setIsChangingParentSku(false);
                            }}>
                            Change SKUs
                        </Button>
                    </Grid>
                </>
            );
        }
        else if (!parentSkuInventory && existingParentSkuProducts) {
            return (
                <>
                    <Grid container>
                        <Grid container item size={12} style={{marginTop: "0.5em", gap: "5px", flexDirection: "column"}}>
                            <Typography style={{fontSize: "14px"}}><b>Parent SKU:</b> {props.product!.parentSku}</Typography>

                            <Grid item style={{flex: 1}}>
                                <Typography style={{fontSize: "14px"}}>Current Product: {props.product!.sku}</Typography>
                                <ProductColumn style={{paddingBottom: "10px"}}
                                               row={{
                                                   sku: props.product!.sku,
                                                   image: props.product!.image,
                                                   name: props.product!.name,
                                                   parentSku: props.product!.parentSku,
                                               }}
                                               column={{id: 0}}
                                               noFilter={true}
                                />
                            </Grid>

                            <Grid container style={{flexDirection: "column"}}>
                                <Typography style={{fontSize: "14px"}}>Found products with same Parent SKU: {props.product!.parentSku}</Typography>

                                <Grid container>
                                    {existingParentSkuProducts.map((product: Product, index: number) => (
                                        <ProductColumn key={index} style={{paddingBottom: "10px"}}
                                                       row={{
                                                           sku: product.sku,
                                                           image: product.image,
                                                           name: product.name,
                                                           parentSku: product.parentSku,
                                                       }}
                                                       column={{id: 0}}
                                                       noFilter={true}
                                />
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Typography variant={"body1"}
                                    style={{display: "block", marginTop: "0.5em"}}>
                            {contextMessage}
                        </Typography>
                    </Grid>
                    <Grid item container justifyContent={"flex-end"}
                          style={{borderTop: "1px solid #DCDCDC", paddingTop: "12px", gap: pageContentGap, width: "100%"}}>
                        <Button variant="text"
                                onClick={() => setOpen(false)}>
                            Cancel
                        </Button>

                        <Button variant="text" color="primary"
                                onClick={() => {
                                    const shouldUpdateInventory = props.forceUniqueRetailerSku
                                        ? changedSku !== ""
                                        : !inventory || (inventory && !inventory.MerchantSKU);
                                    const shouldUpdateParentSkuInventory = props.forceUniqueRetailerSku
                                        ? changedParentSku !== ""
                                        : !parentSkuInventory || (parentSkuInventory && !parentSkuInventory.MerchantSKU);
                                    const status = approveAndList ? "Listing Pending" : "Not Listed";
                                    updateInventoryAndApprove(status, shouldUpdateInventory, shouldUpdateParentSkuInventory);
                                }}>
                            Add to Existing
                        </Button>

                        {(!inventory || !parentSkuInventory) && (
                            <Button variant="text" color="primary"
                                disabled={partnerSettings.autoMatch}
                                onClick={() => {
                                    savedSkuRef.current = changedSku;
                                    savedParentSkuRef.current = changedParentSku;
                                    setChangeSkuDialog(true);
                                    if (!props.forceUniqueRetailerSku) {
                                        setChangedParentSku(props.product!.parentSku + "|" + props.product!.supplierId);
                                        setChangedSku(props.product!.sku);
                                    }
                                    setIsChangingParentSku(true);
                                }}>
                                Create New
                            </Button>
                        )}
                    </Grid>
                </>
            );
        } else {
            return (
                <>
                    <Grid item container justifyContent={"flex-end"}
                          style={{borderTop: "1px solid #DCDCDC", paddingTop: "12px", gap: pageContentGap, width: "100%"}}>
                        <Button variant="text"
                                onClick={() => setOpen(false)}>
                            Cancel
                        </Button>

                        <Button variant="text" color="primary"
                            onClick={() => {
                                const shouldUpdateInventory = props.forceUniqueRetailerSku
                                    ? changedSku !== ""
                                    : !inventory || (inventory && !inventory.MerchantSKU);
                                const shouldUpdateParentSkuInventory = props.forceUniqueRetailerSku
                                    ? changedParentSku !== ""
                                    : !parentSkuInventory || (parentSkuInventory && !parentSkuInventory.MerchantSKU);
                                const status = "Not Listed";
                                updateInventoryAndApprove(status, shouldUpdateInventory, shouldUpdateParentSkuInventory);
                            }}>
                            Approve
                        </Button>

                        <Button variant="text" color="primary"
                            onClick={() => {
                                const shouldUpdateInventory = props.forceUniqueRetailerSku
                                    ? changedSku !== ""
                                    : !inventory || (inventory && !inventory.MerchantSKU);
                                const shouldUpdateParentSkuInventory = props.forceUniqueRetailerSku
                                    ? changedParentSku !== ""
                                    : !parentSkuInventory || (parentSkuInventory && !parentSkuInventory.MerchantSKU);
                                const status = "Listing Pending";
                                updateInventoryAndApprove(status, shouldUpdateInventory, shouldUpdateParentSkuInventory);
                            }}>
                            Approve & List
                        </Button>
                    </Grid>
                </>
            );
        }
    }

    if (props.product === null) return <></>;
    return (
        <Dialog open={open} onClose={() => setOpen(false)}>
            <div style={{
                height: "10px",
                width: "100%",
                background: (duplicateProduct !== null || (!parentSkuInventory && existingParentSkuProducts)) ? theme.palette.error.main : "#22b36e",
                transition: "background 0.5s ease"
            }}/>
            <Grid container style={{padding: "12px", gap: pageContentGap}}>
                <Grid item container alignItems={"center"}>
                    <Grid item container style={{flex: 1, flexDirection: "column"}}>
                        {headerRender()}
                    </Grid>
                </Grid>

                {loading ? (
                    <ContentLoader preserveAspectRatio='none'
                                   style={{height: '2em', width: '100%'}}
                                   primaryColor='#e1e1e1'
                                   secondaryColor='#d8d8d8'/>
                ) : (
                    <>
                        {bodyRender()}
                    </>
                )}

            </Grid>
            {changeSkuDialog && (
                <Dialog open={changeSkuDialog} onClose={() => setChangeSkuDialog(false)}>
                    <div style={{
                        height: "10px",
                        width: "100%",
                        background: duplicateProduct !== null ? theme.palette.error.main : "#22b36e",
                        transition: "background 0.5s ease"
                    }}/>

                    <Grid container style={{padding: "12px", gap: pageContentGap, flexDirection: "column", width: "100%", minWidth: "575px"}}>
                        <Grid item container alignItems={"center"}>
                            <Grid item container style={{flex: 1, flexDirection: "column"}}>
                                <Typography variant={"h5"}>
                                    {duplicateProduct !== null && existingParentSkuProducts
                                        ? "Change SKUs"
                                        : isChangingParentSku
                                            ? "Create New Product"
                                            : "Change SKU"
                                    }
                                </Typography>
                            </Grid>
                        </Grid>

                        {(duplicateProduct !== null && existingParentSkuProducts) ? (
                            <Grid container style={{flexDirection: "column", gap: "1em", marginTop: "1em"}}>
                                <EnhancedTextField label={"Merchant SKU"}
                                                   value={changedSku}
                                                   onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                       setChangedSku(e.target.value)
                                                   }}
                                />
                                <EnhancedTextField label={"Merchant Parent SKU"}
                                                   value={changedParentSku}
                                                   onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                       setChangedParentSku(e.target.value)
                                                   }}
                                />
                            </Grid>
                        ) : (
                            <>
                                {!isChangingParentSku && (
                                    <Grid container style={{marginTop: "1em"}}>
                                        <EnhancedTextField label={"Merchant SKU"}
                                                           value={changedSku}
                                                           onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                               setChangedSku(e.target.value)
                                                           }}
                                        />
                                    </Grid>
                                )}

                                {isChangingParentSku && (
                                    <Grid container style={{marginTop: "1em"}}>
                                        <EnhancedTextField label={"Merchant Parent SKU"}
                                                           value={changedParentSku}
                                                           onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                               setChangedParentSku(e.target.value)
                                                           }}
                                        />
                                    </Grid>
                                )}
                            </>
                        )}

                        <Grid item container justifyContent={"flex-end"}
                              style={{borderTop: "1px solid #DCDCDC", paddingTop: "12px", gap: pageContentGap, width: "100%"}}>
                            <Button variant="text"
                                    onClick={() => {
                                        setChangeSkuDialog(false);
                                        setChangedSku(savedSkuRef.current);
                                        setChangedParentSku(savedParentSkuRef.current);
                                    }}>
                                Cancel
                            </Button>

                            <Button variant="text" color="primary"
                                    onClick={() => {
                                        updateInventoryAndApprove(approveAndList ? "Listing Pending" : "Not Listed", !compareSkus(changedSku, props.product!.sku, partnerSettings.caseSensitive), !compareSkus(changedParentSku, props.product!.parentSku, partnerSettings.caseSensitive));
                                    }}>
                                Update
                            </Button>
                        </Grid>
                    </Grid>
                </Dialog>
            )}
        </Dialog>
    );
}

export default ProductApproveDialog;
